/**
 * Tear down live Stripe Elements and PayPal Buttons before a remount.
 * Always prefer close()/destroy over flipping initialized flags alone —
 * otherwise SPA navigation stacks duplicate widgets.
 */
export function teardownCheckoutPaymentWidgets(
	component,
	{ getElementById = (id) => document.getElementById(id) } = {},
) {
	if (typeof component.destroyStripe === 'function') {
		component.destroyStripe();
	} else {
		component.stripeInitialized = false;
		component.lastStripeInitFingerprint = null;
		component.cardElementReady = false;
	}

	const buttons = component.paypalButtonsInstance;
	if (buttons && typeof buttons.close === 'function') {
		try {
			buttons.close();
		} catch {
			// PayPal close can throw if the iframe was already removed.
		}
	}

	component.paypalButtonsInstance = null;
	component.paypalInitialized = false;
	component.paypalRenderError = false;

	for (const id of ['paypal-button-container', 'paypal-invoice-button', 'paypal-credit-button']) {
		const container = getElementById(id);
		if (container) {
			container.innerHTML = '';
		}
	}
}

/**
 * Serialize async work on a component. Concurrent callers share one in-flight
 * promise and set a pending flag; at most one follow-up run drains after.
 *
 * @param {object} component
 * @param {string} key short lock name (becomes _${key}InFlight / Pending)
 * @param {() => Promise<void>|void} task
 * @returns {Promise<void>}
 */
export function enqueueExclusiveAsync(component, key, task) {
	const inflightKey = `_${key}InFlight`;
	const pendingKey = `_${key}Pending`;

	if (component[inflightKey]) {
		component[pendingKey] = true;

		return component[inflightKey];
	}

	component[inflightKey] = (async () => {
		try {
			await task();
			if (component[pendingKey]) {
				component[pendingKey] = false;
				await task();
			}
		} finally {
			component[inflightKey] = null;
		}
	})();

	return component[inflightKey];
}

/**
 * Mount (or force-remount) checkout payment SDKs under an exclusive lock.
 *
 * Concurrent livewire:navigated remounts share one in-flight promise. A force
 * remount always tears down Stripe (destroyStripe) and PayPal (Buttons.close)
 * before mounting again. Extra force requests that arrive during an in-flight
 * force remount are satisfied by that same remount (no stacked widgets).
 *
 * @param {object} component Alpine checkoutPayment instance
 * @param {{ forceRemount?: boolean }} options
 * @param {{
 *   ensureSdks?: (c: object) => Promise<void>,
 *   getElementById?: (id: string) => HTMLElement|null,
 * }} deps injectable for tests
 */
export function mountCheckoutPaymentSdks(
	component,
	{ forceRemount = false } = {},
	{
		ensureSdks,
		getElementById = (id) => document.getElementById(id),
	} = {},
) {
	if (forceRemount) {
		component._checkoutPaymentForceRemount = true;
	}

	if (component._checkoutPaymentMountInFlight) {
		return component._checkoutPaymentMountInFlight;
	}

	component._checkoutPaymentMountInFlight = (async () => {
		try {
			do {
				if (!component.shouldCollectPayment?.()) {
					component._checkoutPaymentForceRemount = false;
					return;
				}

				const force = !!component._checkoutPaymentForceRemount;
				component._checkoutPaymentForceRemount = false;

				if (force) {
					component.stripeLoadRetries = 0;
					component.stripeBlocked = false;
					component.paypalLoadRetries = 0;
					component.paypalBlocked = false;
					teardownCheckoutPaymentWidgets(component, { getElementById });
				}

				if (typeof ensureSdks === 'function') {
					await ensureSdks(component);
				}

				component.watchStripeReadiness?.();

				// Await initStripe/initPayPal — both may return a Promise while the
				// on-demand SDK script is still loading. Returning before that
				// settles left remount callers (CSP smoke, livewire:navigated)
				// thinking mount finished with no iframe in the DOM.
				if (component.getActiveClientSecret?.() && (!component.stripeInitialized || force)) {
					await component.initStripe?.();
				}

				await component.initPayPal?.();

				// Force remount already produced a clean single widget set — concurrent
				// navigated callers that flipped the force flag mid-flight are done.
				if (force) {
					component._checkoutPaymentForceRemount = false;
					return;
				}
			} while (component._checkoutPaymentForceRemount);
		} finally {
			component._checkoutPaymentMountInFlight = null;
		}

		// Navigated requested force after a non-force mount released the lock.
		if (component._checkoutPaymentForceRemount) {
			await mountCheckoutPaymentSdks(component, { forceRemount: true }, {
				ensureSdks,
				getElementById,
			});
		}
	})();

	return component._checkoutPaymentMountInFlight;
}
